import { useMemo } from "react";
import { CurrencyCode, Order } from "@/types";
import { isSalesOrder } from "@/utils/Orders";
import { useAuth } from "@/contexts/AuthContext";
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Group } from "@mantine/core";
import { useTranslation } from "react-i18next";
import ViewToggle from "../../ViewToggle";
import Table from "@/components/atoms/Table";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import Text from "@/components/atoms/Text";
import Badge from "@/components/atoms/Badge";
import Pagination from "@/components/molecules/Pagination";

import styles from "./styles.module.css";

interface OrderListProps {
  orders: Order[];
  showListAsGrid: boolean;
  setShowListAsGrid: (value: boolean) => void;
  handleActiveOrder: (order: Order) => void;
  deleteOrder: (orderId: number) => void;
  pageNumber: number;
  limit: number;
  totalOrders: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (value: number) => void;
}

export default function OrderList({
  orders,
  showListAsGrid,
  setShowListAsGrid,
  handleActiveOrder,
  deleteOrder,
  pageNumber,
  limit,
  totalOrders,
  onPageChange,
  onRowsPerPageChange,
}: OrderListProps) {
  const { user, currentCompany } = useAuth();
  const { t } = useTranslation();
  const currency = (currentCompany || {}).companyCurrency;

  const columns: ColumnDef<Order>[] = useMemo(
    () => [
      {
        accessorKey: "id",
        id: "id",
        cell: ({ row }) => {
          const order = row.original;
          const salesTypeIcon = isSalesOrder(order) ? "salesIndicatorLarge" : "purchaseIndicatorLarge";
          const name = `${
            user?.language.id && order.salesItem.nameTranslated?.[user.language.id]
              ? order.salesItem.nameTranslated[user.language.id]
              : order.salesItem.salesItem
          } ${order.properties[0] ? `- ${order.properties[0].value}` : ""} ${order.properties[1] ? `- ${order.properties[1].value}` : ""}`;
          const priceText = `${order.price?.[currency as CurrencyCode]} ${currency}/${order?.priceUnit?.unit || ""}`;
          const amountText = `${order.remainingAmount} ${order.unit?.unit || ""}`;

          return (
            <Group justify="space-between" gap={0} wrap="nowrap">
              <Group wrap="nowrap" gap="8px" className={styles.tableOrderDetails}>
                <SVGAsset name={salesTypeIcon} width="26" height="26" />
                <Text strong truncate="end" className={styles.tableOrderName}>
                  {name}
                </Text>
                <Text color="secondary" display="technical" className={styles.tableOrderPrice}>
                  {`${priceText} | ${amountText}`}
                </Text>
              </Group>
              <Group className={styles.tableContractStatusCta}>
                <ListCta onClick={() => deleteOrder(row.original.id)} label={t("orders:cancel")} />
              </Group>
            </Group>
          );
        },
      },
    ],
    [currency, user?.language.id]
  );

  const table = useReactTable({
    data: orders,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className={styles.root}>
      <div className={styles.rootContent}>
        <Group justify="flex-end" className={styles.viewOptions}>
          <ViewToggle showListAsGrid={showListAsGrid} setShowListAsGrid={setShowListAsGrid} />
        </Group>
        <div className={styles.columns}>
          <Table withRowBorders={false} className={styles.tableRoot}>
            <Table.Tbody>
              {table.getRowModel().rows.map((row) => (
                <Table.Tr key={row.id} className={styles.dataRow} onClick={() => handleActiveOrder(row.original)}>
                  {row.getVisibleCells().map((cell) => (
                    <Table.Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
        <Pagination
          activePage={pageNumber}
          totalItems={totalOrders}
          rowsPerPage={limit}
          onRowsPerPageChange={(value) => onRowsPerPageChange(Number(value))}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}

const ListCta = ({ onClick, label }: { onClick: () => void; label: string }) => {
  return (
    <a
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <Badge size="lg" radius={0} color={"gray"} noTextTransform classNames={{ root: styles.tableCta }}>
        {label}
      </Badge>
    </a>
  );
};
